import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { ApiError, AuthService, ConfigService, MessageService } from '../../../../core';
import { BaseTypeahead, BaseTypeaheadService } from '../../../../core/services/typeahead';
import { ConfigCliente } from '../../../configuracoes/app/cliente';
import { ConfigEmpresa, createNomeRepresentante } from '../../../configuracoes/app/empresa';
import { Pessoa } from '../../pessoa';
import { isTipoPessoaCentralCompras } from '../../tipo';
import { ClienteCadastroBadgePipe, ClienteCadastroPipe, clienteCadastroBaixado, clienteCadastroCliente, clienteCadastroOutros, clienteCadastroProspect, isClienteCadastroCliente } from '../cadastro';
import { Cliente } from '../cliente';
import { isClienteRevisaoStatusPendente } from '../cliente-revisao-status';
import { ClienteFilter } from '../filter/cliente-filter';
import { ClienteSearchesHttpService } from '../services';
import { ClienteSituacaoBadgePipe, ClienteSituacaoPipe, clienteSituacaoAtivo, clienteSituacaoBaixado, clienteSituacaoBloqueado, clienteSituacaoCongelado, clienteSituacaoInativo, clienteSituacaoNovo, clienteSituacaoOutros, clienteSituacaoPositivo, clienteSituacaoPreBaixado, clienteSituacaoPreInativo, isClienteSituacaoAtivo } from '../situacao';
import { ClienteTypeahead } from './cliente-typeahead';
import { TypeaheadClienteHttpService } from './typeahead-cliente-http.service';

import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { PessoaEnderecoHttpService } from '../../services';
import { ClienteCentralCompraParticipante } from '../central-compra/participante';
import { ClienteCentralCompraSelectParticipanteModalComponent } from '../central-compra/select-participante-modal';
import { ClienteFilterModalComponent } from '../filter/modal';

/** Typeahead para procurar os clientes mas que o model é um objeto do tipo Pessoa  */
@Component({
    selector: 'sgt-typeahead-cliente-pessoa',
    templateUrl: './typeahead-cliente-pessoa.component.html',
    styleUrls: ['./typeahead-cliente-pessoa.component.less'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TypeaheadClientePessoaComponent), multi: true },
        ClienteCadastroPipe,
        ClienteCadastroBadgePipe,
        ClienteSituacaoPipe,
        ClienteSituacaoBadgePipe
    ]
})
export class TypeaheadClientePessoaComponent implements OnInit, AfterViewInit, BaseTypeahead<Pessoa> {
    configEmpresa: ConfigEmpresa;
    configCliente: ConfigCliente;
    isRepresentante: boolean;

    @Input() entity: Pessoa;
    @Output() ngModelChange = new EventEmitter<Pessoa>();

    /** Cadastro */
    @Input() somenteClientesEmCarteira: boolean = null; // Somente os clientes em carteira (clientes com cadastro igual a cliente)
    @Input() somenteClientesEmCarteiraAtiva: boolean = null; // Somente os clientes em carteira (clientes com cadastro igual a cliente) que estão com situação igual a Ativo
    @Input() somenteClientesEmCarteiraInativa: boolean = null; // Somente os clientes em carteira (clientes com cadastro igual a cliente) que estão com situação igual a Inativo
    @Input() somenteClientes: boolean = null; // Somente os clientes com cadastro igual a cliente
    @Input() somenteClientesProspects: boolean = null; // Somente os clientes com cadastro prospect
    @Input() somenteClientesCongelados: boolean = null; // Somente os clientes com cadastro congelado
    @Input() somenteClientesBloqueados: boolean = null; // Somente os clientes com cadastro bloqueado
    @Input() somenteClientesBaixados: boolean = null; // Somente os clientes com cadastro baixado
    @Input() somenteClientesOutros: boolean = null; // Somente os clientes com cadastro outros
    /** Se deve exibir a mensagem quando o cliente possuir o seu cadastro incompleto */
    @Input() exibirCadastroIncompleto = false;
    /** Se deve obrigar a completar o cadastro incomplento do cliente para emitir o evento de edição do cliente */
    @Input() obrigarCompletarCadastroIncompleto = false;
    /** Mensagem informativa para completar cadastro do cliente */
    @Input() messageCompletarCadastro = 'É necessário complentar o cadastro deste cliente';
    @Input() cadastroProspect = false;
    @Input() cadastroCliente = false;
    @Input() cadastroBaixado = false;
    @Input() cadastroOutros = false;

    /** Situação */
    @Input() somenteClientesAtivos: boolean = null; // Somente os clientes com a situação ativo
    @Input() somenteClientesInativos: boolean = null; // Somente os clientes com a situação inativo
    @Input() situacaoNovo = false;
    @Input() situacaoPositivo = false;
    @Input() situacaoAtivo = false;
    @Input() situacaoPreInativo = false;
    @Input() situacaoInativo = false;
    @Input() situacaoCongelado = false;
    @Input() situacaoBloqueado = false;
    @Input() situacaoPreBaixado = false;
    @Input() situacaoBaixado = false;
    @Input() situacaoOutros = false;

    /** Revisão */
    @Input() exibirRevisaoPendente = false; // Se deve exibir a mensagem quando o cliente estiver com a revisão pendente

    @Input() somenteCentralDeCompraV1: boolean = null; // Se é para considerar somente os clientes que não Centrais de Compra V1
    @Input() somenteCentralDeCompra: boolean = null; // Se é para considerar somente os clientes que não Centrais de Compra
    @Input() idClienteAtual: number = null; // Serve para informar qual é o cliente atual em que o Typeahead está sendo utilizado para pesquisa
    @Input() required: any;
    @Input() disabled: any;
    @Input() readonly: any;
    @Input() ngControl: NgControl;
    @Input() autofocus = false;
    @Input() dropup = false;
    @Input() typeaheadOptionsInScrollableView = 5; // Quantidade de itens visíveis no no scroll do typeahead
    @Output() typeaheadChange = new EventEmitter<Pessoa>();
    @Output() entitySelected = new EventEmitter<Pessoa>(); // Emite evento quando a entidade foi selecionada no typeahead
    @Output() clearEntity = new EventEmitter<any>();

    /** Isenção IPI */
    /** Se deve exibir mensagens referente a Isenção e Destaque do IPI */
    @Input() exibirMensagensIpi = false;

    @ViewChild('inputTypeahead', {static: true}) inputTypeahead: ElementRef;
    typeaheadOptionsLimit = 1000; // Quantidade máxima de registros exibidos no typeahead
    typeaheadSelected: string;
    typeaheadToken: string;
    typeaheadDataSource: Observable<Pessoa[]>;
    typeaheadNoResults: boolean;

    currentRequest: Subscription;
    isLoading: boolean;

    @ViewChild('clienteFilterModal') clienteFilterModal: ClienteFilterModalComponent;

    // Central de Compras
    /** Se deve selecionar um participante quando for selecionado uma central de compras no typeahead */
    @Input() selectParticipanteCentralCompras = false;
    /** Subtitle do modal para selecionar o participante da central de compras */
    @Input() subTitleSelectParticipanteCentralComprasModal: string = null;
    /** Modal para selecionar um participante de uma central de compras */
    @ViewChild('clienteCentralCompraSelectParticipanteModal') clienteCentralCompraSelectParticipanteModal: ClienteCentralCompraSelectParticipanteModalComponent;

    // Insert/Edit cliente
    @Output() insertCliente = new EventEmitter<any>();
    @Output() editCliente = new EventEmitter<number>();

    propagateChange: any = () => {};

    constructor(private authService: AuthService,
                private configService: ConfigService,
                private baseTypeaheadService: BaseTypeaheadService,
                private messageService: MessageService,
                private typeaheadClienteHttpService: TypeaheadClienteHttpService,
                private clienteSearchesHttpService: ClienteSearchesHttpService,
                private pessoaEnderecoHttpService: PessoaEnderecoHttpService) { }

    ngOnInit() {
        this.configEmpresa = this.configService.getEmpresa();
        this.configCliente = this.configService.getCliente();
        this.isRepresentante = this.authService.isRepresentante();

        this.createDataSource();
    }

    ngAfterViewInit() {
        if (this.autofocus) {
            jQuery(this.inputTypeahead.nativeElement).trigger('focus');
        }
    }

    /** Cria o filtro para pesquisar os clientes */
    private createFilter(): ClienteFilter {
        const filter = new ClienteFilter();
        // Nome / Código Integração / CNPJ/CPF
        filter.filterPsq = this.typeaheadToken;

        /** Cadastro */
        filter.somenteClientesEmCarteira = this.somenteClientesEmCarteira;
        filter.somenteClientesEmCarteiraAtiva = this.somenteClientesEmCarteiraAtiva;
        filter.somenteClientesEmCarteiraInativa = this.somenteClientesEmCarteiraInativa;
        filter.somenteClientes = this.somenteClientes;
        filter.somenteClientesProspects = this.somenteClientesProspects;
        filter.somenteClientesCongelados = this.somenteClientesCongelados;
        filter.somenteClientesBloqueados = this.somenteClientesBloqueados;
        filter.somenteClientesBaixados = this.somenteClientesBaixados;
        filter.somenteClientesOutros = this.somenteClientesOutros;
        // Prospect
        if (this.cadastroProspect) {
            filter.cadastros.push(clienteCadastroProspect.id);
        }
        // Cliente
        if (this.cadastroCliente) {
            filter.cadastros.push(clienteCadastroCliente.id);
        }
        // Baixado
        if (this.cadastroBaixado) {
            filter.cadastros.push(clienteCadastroBaixado.id);
        }
        // Outros
        if (this.cadastroOutros) {
            filter.cadastros.push(clienteCadastroOutros.id);
        }

        /** Situação */
        filter.somenteClientesAtivos = this.somenteClientesAtivos;
        filter.somenteClientesInativos = this.somenteClientesInativos;
        // Novo
        if (this.situacaoNovo) {
            filter.situacoes.push(clienteSituacaoNovo.id);
        }
        // Positivo
        if (this.situacaoPositivo) {
            filter.situacoes.push(clienteSituacaoPositivo.id);
        }
        // Ativo
        if (this.situacaoAtivo) {
            filter.situacoes.push(clienteSituacaoAtivo.id);
        }
        // PreInativo
        if (this.situacaoPreInativo) {
            filter.situacoes.push(clienteSituacaoPreInativo.id);
        }
        // Inativo
        if (this.situacaoInativo) {
            filter.situacoes.push(clienteSituacaoInativo.id);
        }
        // Congelado
        if (this.situacaoCongelado) {
            filter.situacoes.push(clienteSituacaoCongelado.id);
        }
        // Bloqueado
        if (this.situacaoBloqueado) {
            filter.situacoes.push(clienteSituacaoBloqueado.id);
        }
        // PreBaixado
        if (this.situacaoPreBaixado) {
            filter.situacoes.push(clienteSituacaoPreBaixado.id);
        }
        // Baixado
        if (this.situacaoBaixado) {
            filter.situacoes.push(clienteSituacaoBaixado.id);
        }
        // Outros
        if (this.situacaoOutros) {
            filter.situacoes.push(clienteSituacaoOutros.id);
        }

        // Somente central de compra V1
        filter.somenteCentralDeCompraV1 = this.somenteCentralDeCompraV1;
        // Somente central de compra
        filter.somenteCentralDeCompra = this.somenteCentralDeCompra;
        // Cliente atual
        filter.idClienteAtual = this.idClienteAtual;

        return filter;
    }

    /** Cria o data source do typeahead */
    createDataSource() {
        this.typeaheadDataSource = Observable.create((observer: any) => {
            if (this.currentRequest) {
                this.currentRequest.unsubscribe();
            }

            const filter = this.createFilter();
            this.currentRequest = this.typeaheadClienteHttpService.search(filter, this.typeaheadOptionsLimit)
                                    .subscribe(
                                        (clientes: ClienteTypeahead[]) => observer.next(clientes),
                                        (error: ApiError) => {
                                            this.messageService.error(error.message, 'Ocorreu um erro ao pesquisar os clientes. Tente novamente', error);
                                            observer.next([]);
                                        }
                                    );
        }).pipe(
            map((clientes: ClienteTypeahead[]) => {
                const pessoas: Pessoa[] = [];
                clientes.forEach(cliente => {
                    this.mapClienteToPessoa(cliente);
                    pessoas.push(cliente.pessoa);
                });
                return pessoas;
            })
        );
    }

    /** Mapeia os dados do cliente para a sua pessoa */
    private mapClienteToPessoa(cliente: ClienteTypeahead) {
        cliente.pessoa.idCliente = cliente.id;
        cliente.pessoa.idRepresentante = cliente.idRepresentante;
        cliente.pessoa.codigoIntegracaoErpRepresentante = cliente.codigoIntegracaoErpRepresentante;
        cliente.pessoa.nomeRepresentante = createNomeRepresentante(this.configEmpresa.identificacaoRepresentante, cliente.nomeRepresentante, cliente.nomeFantasiaRepresentante);
        cliente.pessoa.nomeCidade = cliente.nomeCidade;
        cliente.pessoa.uf = cliente.uf;
        cliente.pessoa.cadastroCliente = cliente.cadastro;
        cliente.pessoa.situacaoCliente = cliente.situacao;
        cliente.pessoa.cadastroCompletoCliente = cliente.cadastroCompleto;
        cliente.pessoa.revisaoStatusCliente = cliente.revisaoStatus;
        cliente.pessoa.isentoIpiCliente = cliente.isentoIpi;
        cliente.pessoa.dataValidadeCartaIsencaoIpiCliente = cliente.dataValidadeCartaIsencaoIpi;
        cliente.pessoa.cartaIsencaoIpiVencidaCliente = cliente.cartaIsencaoIpiVencida;
        cliente.pessoa.rotaEntregaErpSCR = cliente.rotaEntregaErpSCR;
    }

    /** Carrega o cliente informado para o Typeahead */
    private load(idCliente: number): Promise<ClienteTypeahead> {
        return new Promise<ClienteTypeahead>((resolve, reject) => {
            this.typeaheadClienteHttpService.load(idCliente)
                .pipe(
                    take(1)
                )
                .subscribe(
                    cliente => {
                        this.mapClienteToPessoa(cliente);
                        resolve(cliente);
                    },
                    (error: ApiError) => {
                        this.messageService.error(error.message, 'Erro ao carregar o cliente', error);
                        reject();
                    }
                );
        });
    }

    /** Carrega o cliente informado para o Typeahead através da sua pessoa*/
    private loadByPessoa(idPessoa: number): Promise<ClienteTypeahead> {
        return new Promise<ClienteTypeahead>((resolve, reject) => {
            this.typeaheadClienteHttpService.loadByPessoa(idPessoa)
                .pipe(
                    take(1)
                )
                .subscribe(
                    cliente => {
                        this.mapClienteToPessoa(cliente);
                        resolve(cliente);
                    },
                    (error: ApiError) => {
                        this.messageService.error(error.message, 'Erro ao carregar o cliente', error);
                        reject();
                    }
                )
        });
    }

    /** Atualiza os dados do cliente selecionado no typeahead */
    private updateEntity(cliente: Cliente) {
        if (cliente && cliente.pessoa) {
            this.entity = cliente.pessoa;
            this.entity.idCliente = cliente.id;
            this.entity.idRepresentante = cliente.idRepresentante;
            this.entity.cadastroCliente = cliente.cadastro;
            this.entity.situacaoCliente = cliente.situacao;
            this.entity.cadastroCompletoCliente = cliente.cadastroCompleto;
            this.entity.revisaoStatusCliente = cliente.revisaoStatus;
            this.selectEntity(this.entity);
        } else {
            this.selectEntity(null);
        }
    }

    /** Ao digitar no input do typeahead */
    onKeyup(e: KeyboardEvent) {
        this.baseTypeaheadService.onKeyup(this, e);
    }

    /** Ao sair do input do typeahead */
    onBlur() {
        this.baseTypeaheadService.onBlur(this);
        if (!this.entity) {
            this.clearEntity.emit();
        }
    }


    /** Verifica se o typeahead possui resultados */
    onChangeTypeaheadNoResults(noResults: boolean) {
        this.typeaheadNoResults = noResults;
    }

    /** Seta o cliente informado no typeahead */
    setEntity(idCliente: number) {
        this.load(idCliente)
            .then(cliente => {
                if (this.selectParticipanteCentralCompras && isTipoPessoaCentralCompras(cliente?.pessoa?.tipo)) { // Central compras
                    this.onSelectCentralCompras(cliente?.id);
                } else { // Cliente
                    this.selectEntity(cliente?.pessoa ?? null);
                }
            })
            .catch(() => {});
    }

    /** Seleciona o cliente no typeahead */
    private selectEntity(pessoa: Pessoa) {
        this.entity = pessoa ?? null;

        this.propagateChange(this.entity);
        this.typeaheadChange.emit(this.entity);
        this.entitySelected.emit(this.entity);
        this.writeValue(this.entity);
        jQuery(this.inputTypeahead.nativeElement).trigger('focus');

        // Completar cadastro
        this.execCompletarCadastro();
    }

    /** Ao selecionar a Pessoa no typeahead */
    onSelectEntity(tm: TypeaheadMatch) {
        const pessoa = tm.item as Pessoa;
        if (this.selectParticipanteCentralCompras && isTipoPessoaCentralCompras(pessoa?.tipo)) { // Selecionado central compras
            this.onSelectCentralCompras(pessoa?.idCliente);
        } else { // Selecionado cliente
            this.baseTypeaheadService.onSelectEntity(this, tm);
            this.entitySelected.emit(this.entity);

            // Completar cadastro
            this.execCompletarCadastro();
        }
    }

    /** Ao selecionar o cliente no modal de pesquisa */
    onSelectEntityModal(cliente: Cliente) {
        if (this.selectParticipanteCentralCompras && isTipoPessoaCentralCompras(cliente?.pessoa?.tipo)) { // Selecionado central compras
            this.onSelectCentralCompras(cliente?.id);
        } else { // Selecionado cliente
            // Atualizando cliente selecionado via modal no typeahead
            this.updateEntity(cliente);
        }
    }

    /** Abre o modal de pesquisa */
    search() {
        this.clienteFilterModal.open();
    }

    /** Limpa o typeahead e o model */
    clear() {
        this.baseTypeaheadService.clear(this);
        this.clearEntity.emit();
    }

    /** Seta o foco no input */
    setFocus() {
        jQuery(this.inputTypeahead.nativeElement).trigger('focus');
    }

   /** Quando recebe um objeto Pessoa de "fora" do component */
    async writeValue(value: Pessoa) {
        if (value) {
            // Se não possuir o cliente então carregamos ele através da sua pessoa
            if (!value.idCliente) {
                const cliente = await this.loadByPessoa(value.id);
                value = cliente.pessoa;
            }

            this.baseTypeaheadService.writeValue(this, value, value.nome);

            // Carregando os dados do cliente
            this.findCliente(value.id);
            // Obtendo a cidade e estado da pessoa do cliente para exibir no template
            this.getCidadeEstadoPessoa(value.id);
        } else {
            this.baseTypeaheadService.writeValue(this, null);
        }
    }

    /** Propaga as alterações */
    registerOnChange(fn) {
        this.propagateChange = fn;
    }

    registerOnTouched() {}

    /** Carrega o cliente através da sua pessoa */
    private findCliente(idPessoa: number) {
        this.clienteSearchesHttpService.findByPessoa(idPessoa, null)
            .pipe(
                take(1)
            )
            .subscribe(
                cliente => {
                    this.entity.rotaEntregaErpSCR = cliente?.rotaEntregaErpSCR ?? null;
                },
                (() => {})
            );
    }

    /** Obtem o nome da cidade e estado da pessoa */
    private getCidadeEstadoPessoa(idPessoa: number) {
        this.pessoaEnderecoHttpService.getCidadeEstado(idPessoa)
            .pipe(
                take(1)
            )
            .subscribe(
                entity => {
                    if (entity) {
                        this.entity.nomeCidade = entity.nomeCidade;
                        this.entity.uf = entity.uf;
                    }
                },
                (() => {})
            );
    }

    /** Verifica se é um cliente com cadastro igual a Cliente */
    isClienteCadastroCliente(cadastro: number): boolean {
        return isClienteCadastroCliente(cadastro);
    }

    /** Verifica se é um cliente com situação ativo */
    isClienteSituacaoAtivo(situacao: number): boolean {
        return isClienteSituacaoAtivo(situacao);
    }

    /** Verifica se deve exibir a mensagem quando o cliente possuir o seu cadastro incompleto */
    isExibirCardCadastroIncompleto(): boolean {
        return this.exibirCadastroIncompleto && (this.entity?.id && !this.entity?.cadastroCompletoCliente);
    }

    /** Emite evento para editar cliente quando for obrigatório completar o cadastro incompleto do cliente */
    private execCompletarCadastro() {
        // Se for obrigatório e o cliente estiver com o cadastro incompleto
        if (this.obrigarCompletarCadastroIncompleto && !this.entity?.cadastroCompletoCliente) {
            this.editCliente.emit(this.entity?.idCliente);
        }
    }

    /** Emite evento para inserir um novo cliente */
    insertClienteClick() {
        this.insertCliente.emit();
    }

    /** Adiciona o cliente inserido ao typeahead */
    afterInsertCliente(cliente: Cliente) {
        this.updateEntity(cliente);
    }

    /** Emite evento para editar o cliente selecionado no typeahead */
    editClienteClick() {
        this.editCliente.emit(this.entity?.idCliente);
    }

    /** Atualiza o cliente editado no typeahead */
    afterEditCliente(cliente: Cliente) {
        this.updateEntity(cliente);
    }

    /** Verifica se o status da revisão do cliente é Pendente */
    private isRevisaoPendente(): boolean {
        return isClienteRevisaoStatusPendente(this.entity?.revisaoStatusCliente);
    }

    /** Verifica se deve exibir o card com a mensagem de cliente com revisão pendente */
    isExibirCardRevisaoPendente(): boolean {
        return this.exibirRevisaoPendente && this.isRevisaoPendente();
    }

    /** Ao selecionar um cliente central de compras abrimos o modal para selecionar um participante desta central de compras */
    private onSelectCentralCompras(idClienteCentralCompras: number) {
        this.clienteCentralCompraSelectParticipanteModal?.open(idClienteCentralCompras);
        this.clear();
    }

    /** Ao selecionar um participante da central de compras setamos este no pedido */
    onSelectParticipanteCentralCompras(participante: ClienteCentralCompraParticipante) {
        this.load(participante.id)
            .then(cliente => {
                this.selectEntity(cliente?.pessoa ?? null);
            })
            .catch(() => {});
    }

    /** Ao cancelar a seleção de um participante da central de compras removemos o cliente/pessoa do pedido */
    onCancelSelectParticipanteCentralCompras() {
        this.clear();
    }

    /** Verifica se deve exibir as mensagens referentes a isenção de IPI */
    private isShowCardIsencaoIpi(): boolean {
        // Se não estiver selecionado o cliente
        if (!this.entity) {
            return false;
        }
        // Se não for para exibir as mensagens do IPI no typeahead
        if (!this.exibirMensagensIpi) {
            return false;
        }

        return true;
    }

    /** Verifica se deve exibir a mensagem de Carta de Isenção do IPI Vencida */
    isShowCardCartaIsencaoIpiVencida(): boolean {
        if (!this.isShowCardIsencaoIpi()) {
            return false;
        }

        if (this.entity?.cartaIsencaoIpiVencidaCliente == null || this.entity?.cartaIsencaoIpiVencidaCliente === undefined) {
            return false;
        }

        return true;
    }

    /** Verifica se deve exibir a mensagem de Destaque do IPI */
    isShowCardDestaqueIpi(): boolean {
        if (!this.isShowCardIsencaoIpi()) {
            return false;
        }

        if (this.entity?.isentoIpiCliente == null || this.entity?.isentoIpiCliente === undefined) {
            return false;
        }

        return true;
    }
}
